import { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { get, put, ApiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ALL_SIZES } from '@/lib/product-config';

// coreGarments shape (supplier-catalog lib/core-garments.js). Edits are
// VALUES-only: the server's applyGarmentEdits keeps the tier/position
// structure, order and identity fields no matter what we send.
type PrintPosition = {
  position?: string;
  display?: string;
  code: string;
  printWidth?: number;
  printHeight?: number;
  defaultLogoPos?: { x?: number; y?: number };
  defaultMaxW?: number;
  defaultMaxH?: number;
};
type Garment = {
  id: string;
  name?: string;
  category?: string;
  productType?: string;
  supplier?: string;
  styleNumbers?: Record<string, string>;
  fabricComposition?: string;
  sizes?: string[];
  tiers?: Record<string, { price?: number }>;
  printPositions?: PrintPosition[];
  decorationMethod?: string;
  decorationDetail?: string;
  active?: boolean;
  order?: number;
};
type Payload = { success?: boolean; count?: number; garments?: Garment[]; source?: string };

const GARMENTS_KEY = '/api/garments';
const inputCls = 'w-full rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]';

function tierSummary(g: Garment) {
  return Object.entries(g.tiers || {})
    .map(([name, t]) => `${name} $${t?.price ?? '—'}`)
    .join(' · ');
}

function num(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export default function SupplierCatalog() {
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState<Garment | null>(null);
  const [saving, setSaving] = useState(false);
  const { data, isLoading, error, mutate } = useSWR<Payload>(GARMENTS_KEY, get);

  const filtered = (data?.garments || []).filter((g) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      String(g.name || '').toLowerCase().includes(needle) ||
      String(g.supplier || '').toLowerCase().includes(needle) ||
      String(g.category || '').toLowerCase().includes(needle) ||
      String(g.id || '').toLowerCase().includes(needle) ||
      Object.values(g.styleNumbers || {}).some((s) => String(s).toLowerCase().includes(needle))
    );
  });

  function openEdit(g: Garment) {
    setDraft(JSON.parse(JSON.stringify(g)) as Garment);
  }

  function patchTier(tier: string, price: number | undefined) {
    setDraft((d) => (d ? { ...d, tiers: { ...(d.tiers || {}), [tier]: { ...(d.tiers?.[tier] || {}), price } } } : d));
  }

  function toggleSize(size: string) {
    setDraft((d) => {
      if (!d) return d;
      const sizes = d.sizes || [];
      return { ...d, sizes: sizes.includes(size) ? sizes.filter((s) => s !== size) : [...sizes, size] };
    });
  }

  function patchPosition(code: string, field: string, value: number | undefined) {
    setDraft((d) => {
      if (!d) return d;
      const positions = (d.printPositions || []).map((p) => {
        if (p.code !== code) return p;
        if (field === 'x' || field === 'y') {
          return { ...p, defaultLogoPos: { ...(p.defaultLogoPos || {}), [field]: value } };
        }
        return { ...p, [field]: value };
      });
      return { ...d, printPositions: positions };
    });
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      // Only the value fields applyGarmentEdits consumes — structure/identity
      // stay server-owned.
      await put(`/api/garments/${encodeURIComponent(draft.id)}`, {
        tiers: draft.tiers,
        sizes: draft.sizes,
        printPositions: (draft.printPositions || []).map((p) => ({
          code: p.code,
          printWidth: p.printWidth,
          printHeight: p.printHeight,
          defaultLogoPos: p.defaultLogoPos,
          defaultMaxW: p.defaultMaxW,
          defaultMaxH: p.defaultMaxH,
        })),
        active: draft.active !== false,
        order: draft.order,
      });
      toast.success(`Garment "${draft.name || draft.id}" saved — applies to newly generated products`);
      setDraft(null);
      await mutate();
    } catch (e) {
      const body = (e as ApiError).body as { errors?: string[]; error?: string; message?: string } | null;
      toast.error(body?.errors?.join('; ') || body?.message || body?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // Show ALL_SIZES plus anything unusual the garment already carries.
  const sizeOptions = draft
    ? [...ALL_SIZES, ...(draft.sizes || []).filter((s) => !(ALL_SIZES as readonly string[]).includes(s))]
    : [];

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Supplier Catalog</h1>
      <p className="mb-3 text-[12px] text-muted">
        Base team-store garments ({data?.count ?? data?.garments?.length ?? 0}) · edit tier prices, sizes and print dimensions — changes apply to newly generated products, no deploy
      </p>

      <div className="mb-3 max-w-[360px]">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, supplier, style, category…" />
      </div>

      {error ? (
        <div className="rounded-md bg-danger/10 p-4 text-[12px] text-danger">
          Couldn't load garments: {(error as Error)?.message || ''}
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : filtered.length ? (
        <div className="rounded-md bg-surface shadow-card">
          {filtered.map((g) => (
            <div key={g.id} className={`flex items-center gap-3 border-b border-divider px-3 py-2 last:border-b-0 ${g.active === false ? 'opacity-60' : ''}`}>
              <span className="font-mono text-[10px] text-subtle w-20 shrink-0 truncate">{g.id}</span>
              <span className="min-w-0 flex-1 truncate text-[12px]">
                {g.name || 'Unnamed'}
                {g.fabricComposition && <span className="ml-2 text-muted">· {g.fabricComposition}</span>}
              </span>
              {g.active === false && <Badge className="bg-warning/10 text-warning">inactive</Badge>}
              {g.supplier && <Badge className="bg-bg text-muted">{g.supplier}</Badge>}
              {g.category && <Badge className="bg-primary/10 text-primary">{g.category}</Badge>}
              <span className="hidden shrink-0 text-right text-[11px] font-mono text-text sm:block">{tierSummary(g)}</span>
              <Button size="sm" variant="secondary" onClick={() => openEdit(g)}>Edit</Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md bg-surface p-8 text-center text-[12px] text-muted shadow-card">No garments match "{q}".</div>
      )}

      <Dialog open={!!draft} onOpenChange={(o) => { if (!o && !saving) setDraft(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[520px]">
          {draft && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[14px]">Edit {draft.name || draft.id}</DialogTitle>
                <DialogDescription className="text-[11px]">
                  {draft.supplier} · {Object.entries(draft.styleNumbers || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || 'no style numbers'} · values only — tiers and positions can't be added or removed here
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <h3 className="mb-1 text-[11px] uppercase tracking-wide text-muted">Tier prices</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(draft.tiers || {}).map(([tier, t]) => (
                      <label key={tier} className="block">
                        <span className="mb-1 block text-[11px] text-muted">{tier} ($)</span>
                        <input
                          className={inputCls}
                          type="number"
                          step="1"
                          value={t?.price ?? ''}
                          onChange={(e) => patchTier(tier, num(e.target.value))}
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="mb-1 text-[11px] uppercase tracking-wide text-muted">Sizes</h3>
                  <div className="flex flex-wrap gap-1">
                    {sizeOptions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleSize(s)}
                        className={`rounded-sm border px-2 py-0.5 text-[11px] ${(draft.sizes || []).includes(s) ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-bg text-muted'}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="mb-1 text-[11px] uppercase tracking-wide text-muted">Print positions</h3>
                  <div className="space-y-2">
                    {(draft.printPositions || []).map((p) => (
                      <fieldset key={p.code} className="rounded-md border border-divider p-2">
                        <legend className="px-1 text-[11px] text-muted">{p.display || p.position} ({p.code})</legend>
                        <div className="grid grid-cols-3 gap-2">
                          <label className="block">
                            <span className="mb-1 block text-[10px] text-muted">Width (in)</span>
                            <input className={inputCls} type="number" step="0.1" value={p.printWidth ?? ''} onChange={(e) => patchPosition(p.code, 'printWidth', num(e.target.value))} />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[10px] text-muted">Height (in)</span>
                            <input className={inputCls} type="number" step="0.1" value={p.printHeight ?? ''} onChange={(e) => patchPosition(p.code, 'printHeight', num(e.target.value))} />
                          </label>
                          <span />
                          <label className="block">
                            <span className="mb-1 block text-[10px] text-muted">Logo X (px)</span>
                            <input className={inputCls} type="number" value={p.defaultLogoPos?.x ?? ''} onChange={(e) => patchPosition(p.code, 'x', num(e.target.value))} />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[10px] text-muted">Logo Y (px)</span>
                            <input className={inputCls} type="number" value={p.defaultLogoPos?.y ?? ''} onChange={(e) => patchPosition(p.code, 'y', num(e.target.value))} />
                          </label>
                          <span />
                          <label className="block">
                            <span className="mb-1 block text-[10px] text-muted">Max W (px)</span>
                            <input className={inputCls} type="number" value={p.defaultMaxW ?? ''} onChange={(e) => patchPosition(p.code, 'defaultMaxW', num(e.target.value))} />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[10px] text-muted">Max H (px)</span>
                            <input className={inputCls} type="number" value={p.defaultMaxH ?? ''} onChange={(e) => patchPosition(p.code, 'defaultMaxH', num(e.target.value))} />
                          </label>
                        </div>
                      </fieldset>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-[12px]">
                    <input type="checkbox" checked={draft.active !== false} onChange={(e) => setDraft((d) => (d ? { ...d, active: e.target.checked } : d))} />
                    Active
                  </label>
                  <label className="flex items-center gap-2 text-[12px]">
                    Display order
                    <input
                      className={`${inputCls} w-20`}
                      type="number"
                      step="1"
                      value={draft.order ?? ''}
                      onChange={(e) => setDraft((d) => (d ? { ...d, order: num(e.target.value) } : d))}
                    />
                  </label>
                </div>
              </div>

              <DialogFooter>
                <Button size="sm" variant="ghost" disabled={saving} onClick={() => setDraft(null)}>Cancel</Button>
                <Button size="sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save garment'}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
